import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { EmptyValue } from '@/components/common/EmptyValue';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { TEACHER_CARD_CLASS } from '../constants/teacherSubmission.constants';

const CARD_HEADER_CLASS = 'flex items-center gap-2 border-b px-4';

const CARD_BODY_CLASS = 'px-4 py-1';

const SKELETON_VALUE_WIDTH_CLASSES = ['w-28', 'w-20', 'w-32', 'w-24'] as const;

interface TeacherInfoCardProps {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  bodyClassName?: string;
  className?: string | undefined;
}

export function TeacherInfoCard({
  icon: Icon,
  title,
  children,
  bodyClassName,
  className,
}: TeacherInfoCardProps) {
  return (
    <section className={cn(TEACHER_CARD_CLASS, className)}>
      <header className={cn(CARD_HEADER_CLASS, 'bg-info/60 py-2.5')}>
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-semibold">{title}</h3>
      </header>
      <div className={cn(CARD_BODY_CLASS, bodyClassName)}>{children}</div>
    </section>
  );
}

interface TeacherInfoCardSkeletonProps {
  children: ReactNode;
  bodyClassName?: string;
}

export function TeacherInfoCardSkeleton({ children, bodyClassName }: TeacherInfoCardSkeletonProps) {
  return (
    <div className={TEACHER_CARD_CLASS} aria-hidden>
      <div className={cn(CARD_HEADER_CLASS, 'py-3')}>
        <Skeleton className="size-4 rounded" />
        <Skeleton className="h-4 w-28" />
      </div>
      <div className={cn(CARD_BODY_CLASS, bodyClassName)}>{children}</div>
    </div>
  );
}

interface TeacherInfoRowProps {
  label: string;
  children: ReactNode;
}

export function TeacherInfoRow({ label, children }: TeacherInfoRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2 text-sm last:border-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium wrap-break-word">
        {children ?? <EmptyValue />}
      </dd>
    </div>
  );
}

interface TeacherInfoRowsSkeletonProps {
  rowCount: number;
}

export function TeacherInfoRowsSkeleton({ rowCount }: TeacherInfoRowsSkeletonProps) {
  return (
    <div aria-hidden>
      {Array.from({ length: rowCount }, (_, index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-4 border-b py-2.5 last:border-0"
        >
          <Skeleton className="h-4 w-24" />
          <Skeleton
            className={cn(
              'h-4',
              SKELETON_VALUE_WIDTH_CLASSES[index % SKELETON_VALUE_WIDTH_CLASSES.length],
            )}
          />
        </div>
      ))}
    </div>
  );
}
